import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { adminRoutesArray, generalRoutes, generalRoutesArray, landingRoutesArray, propertyRoutesArray, orgRoutesArray, systemRoutesArray, exploreRoutesArray, paymentRoutesArray, accountRoutes, accountRoutesArray, landingRoutes, hasAnyAdminPermission, } from './common/routes';
import { routeIsRender } from './_common';
import { ToastProvider } from './contexts/toast';
import E404 from './screens/system/no-layout-404';
import Admin404 from './screens/system/admin-404';
import AppCommon from './AppCommon';
const routingConfig = {
    'routeSets': [
        {
            'name': 'account',
            'available': () => [...accountRoutesArray],
            'description': 'User-account pages (account, membership)',
            'onInvalidAccess': [],
        },
        {
            'name': 'general',
            'available': () => generalRoutesArray,
            'description': 'Always available (login, signup, etc.)',
            'onInvalidAccess': [],
        },
        {
            'name': 'landing',
            'available': () => landingRoutesArray,
            'description': 'Landing pages (only on root domain)',
            'onInvalidAccess': [],
        },
        {
            'name': 'explore',
            'available': () => exploreRoutesArray,
            'description': 'Explore Feature routes',
            'onInvalidAccess': [],
        },
        {
            'available': () => paymentRoutesArray,
            'onInvalidAccess': [],
        },
        {
            'name': 'property',
            'available': () => [...propertyRoutesArray, ...orgRoutesArray],
            'description': 'Property pages',
            'onInvalidAccess': [],
        },
        {
            'name': 'admin',
            // Gate the entire admin route set on holding at least one admin
            // permission. The hub routes (`manage`, `propertiesHub`, etc.) have
            // empty permission lists so any admin can land on a shell that
            // shows what they can access — but without ANY admin permission
            // those hubs are dead-end shells. Skip registering the routes for
            // non-admins entirely; the 'admin-no-permissions' redirect below
            // catches their URL visits and sends them to /account.
            'available': (ctx) => {
                if (!ctx.isAuthenticated)
                    return [];
                if (!hasAnyAdminPermission([adminRoutesArray, systemRoutesArray], ctx.hasPermission))
                    return [];
                return adminRoutesArray.filter(r => r.permissions.length === 0 || ctx.hasPermission(r.permissions));
            },
            'description': 'Admin panel (requires auth + at least one admin permission)',
            'onInvalidAccess': [
                {
                    name: 'protected-without-auth',
                    when: (ctx) => !ctx.matched && !ctx.isAuthenticated && adminRoutesArray.some(r => ctx.currentPath[0] === r.path[0]),
                    action: 'redirect',
                    target: () => generalRoutes.login.getURL(),
                    description: 'Redirect protected admin routes to login'
                },
                {
                    name: 'admin-no-permissions',
                    when: (ctx) => !ctx.matched && ctx.isAuthenticated && ctx.currentPath[0] === 'manage' &&
                        !hasAnyAdminPermission([adminRoutesArray, systemRoutesArray], ctx.hasPermission),
                    action: 'redirect',
                    target: () => accountRoutes.accountDefault.getURL(),
                    description: 'Send authenticated non-admin users from /manage to /account'
                },
                {
                    name: 'admin-not-found',
                    when: (ctx) => !ctx.matched && ctx.isAuthenticated && ctx.currentPath[0] === 'manage',
                    action: 'render',
                    component: Admin404,
                    description: 'Show admin 404 for authenticated admin users hitting an unknown /manage URL'
                }
            ],
        },
        {
            'name': 'system',
            'available': (ctx) => {
                return (ctx.isAuthenticated
                    ? systemRoutesArray.filter(r => r.permissions.length === 0 || ctx.hasPermission(r.permissions))
                    : []);
            },
            'description': 'System admin (requires auth + system permissions)',
            'onInvalidAccess': [
                {
                    name: 'system-without-auth',
                    when: (ctx) => !ctx.matched && !ctx.isAuthenticated && ctx.currentPath[0] === 'system',
                    action: 'redirect',
                    target: () => generalRoutes.login.getURL(),
                    description: 'Redirect system routes to login'
                }
            ],
        }
    ],
    defaultFallback: {
        name: 'default-not-found',
        when: (ctx) => !ctx.matched,
        action: 'render',
        component: () => _jsx(E404, { urlHome: landingRoutes.root.getURL() }),
        description: 'Default 404 for all unmatched routes'
    },
    /**
     * Error Handlers - Explicit handlers for HTTP errors and route failures
     * Must be explicitly defined even if they delegate to helper functions
     */
    on401: () => {
        localStorage.removeItem('userToken');
        window.location.href = generalRoutes.login.getURL();
    },
    on404: (ctx) => {
        var _a, _b;
        return (_b = (_a = routingConfig
            .routeSets
            .flatMap(set => set.onInvalidAccess)
            .filter(h => h.when(ctx))
            .find(routeIsRender)) === null || _a === void 0 ? void 0 : _a.component) !== null && _b !== void 0 ? _b : routingConfig.defaultFallback.component;
    },
};
const App = () => {
    return (_jsx(AppCommon, { routingConfig: routingConfig, wrapper: (children) => _jsx(_Fragment, { children: _jsx(ToastProvider, { children: children }) }) }));
};
export default App;
